/**
 * API Key Management Utility
 * Handles API key from environment, localStorage, and URL parameters
 */

const API_KEY_STORAGE_KEY = 'appdesko_api_key';

export class ApiKeyManager {
  /**
   * Get API key from URL parameter and save to localStorage
   */
  static saveApiKeyFromUrl(): string | null {
    try {
      console.log('🔍 Checking URL for API key...', window.location.search);
      const urlParams = new URLSearchParams(window.location.search);
      const apiKeyFromUrl = urlParams.get('k');
      console.log('🔑 API key from URL:', apiKeyFromUrl);
      
      if (apiKeyFromUrl) {
        console.log('💾 Saving API key to localStorage...');
        localStorage.setItem(API_KEY_STORAGE_KEY, apiKeyFromUrl);
        
        // Verify it was saved
        const saved = localStorage.getItem(API_KEY_STORAGE_KEY);
        console.log('✅ API key saved to localStorage:', saved);
        
        // Clean up URL by removing the 'k' parameter
        urlParams.delete('k');
        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        console.log('🧹 Cleaning URL from:', window.location.href, 'to:', newUrl);
        window.history.replaceState({}, document.title, newUrl);
        
        return apiKeyFromUrl;
      }
      
      console.log('❌ No API key found in URL');
      return null;
    } catch (error) {
      console.warn('❌ Error saving API key from URL:', error);
      return null;
    }
  }

  /**
   * Get API key from localStorage
   */
  static getApiKeyFromStorage(): string | null {
    try {
      const stored = localStorage.getItem(API_KEY_STORAGE_KEY);
      console.log('📦 Reading from localStorage:', { key: API_KEY_STORAGE_KEY, value: stored });
      return stored;
    } catch (error) {
      console.warn('❌ Error reading API key from localStorage:', error);
      return null;
    }
  }

  /**
   * Save API key to localStorage
   */
  static saveApiKeyToStorage(apiKey: string): void {
    try {
      localStorage.setItem(API_KEY_STORAGE_KEY, apiKey);
    } catch (error) {
      console.warn('Error saving API key to localStorage:', error);
    }
  }

  /**
   * Get API key with priority: URL param > localStorage > env variable
   */
  static getApiKey(): string | null {
    console.log('🔑 Getting API key...');
    console.log('🌐 Current URL:', window.location.href);
    console.log('📦 localStorage keys:', Object.keys(localStorage));
    
    // First check if there's an API key in URL and save it
    const urlApiKey = this.saveApiKeyFromUrl();
    if (urlApiKey) {
      console.log('✅ Using API key from URL:', urlApiKey);
      return urlApiKey;
    }

    // Then check localStorage
    const storageApiKey = this.getApiKeyFromStorage();
    if (storageApiKey) {
      console.log('✅ Using API key from localStorage:', storageApiKey);
      return storageApiKey;
    }

    // Finally fallback to environment variable
    const envApiKey = process.env.REACT_APP_API_KEY;
    if (envApiKey) {
      console.log('✅ Using API key from environment:', envApiKey);
      return envApiKey;
    }

    console.log('❌ No API key found in any source');
    console.log('📊 Debug info:', {
      url_search: window.location.search,
      localStorage_keys: Object.keys(localStorage),
      env_var: !!process.env.REACT_APP_API_KEY
    });
    return null;
  }

  /**
   * Clear API key from localStorage
   */
  static clearApiKey(): void {
    try {
      localStorage.removeItem(API_KEY_STORAGE_KEY);
    } catch (error) {
      console.warn('Error clearing API key from localStorage:', error);
    }
  }

  /**
   * Check if API key is available
   */
  static hasApiKey(): boolean {
    return !!this.getApiKey();
  }

  /**
   * Get API key source for debugging
   */
  static getApiKeySource(): 'url' | 'localStorage' | 'environment' | 'none' {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('k')) {
      return 'url';
    }

    if (this.getApiKeyFromStorage()) {
      return 'localStorage';
    }

    if (process.env.REACT_APP_API_KEY) {
      return 'environment';
    }

    return 'none';
  }
}